import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  ChartBarIcon,
  BookOpenIcon,
  UserIcon,
  PowerIcon,
  Bars3Icon,
  XMarkIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';

const CommonHeader = ({ 
  title, 
  subtitle, 
  showBackButton = false, 
  customBackAction,
  rightContent 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    if (window.confirm('정말 로그아웃하시겠습니까?')) {
      try {
        await logout();
        navigate('/');
      } catch (error) {
        console.error('Logout failed:', error);
        alert('로그아웃 중 오류가 발생했습니다.');
      }
    }
  };

  const handleBackClick = () => {
    if (customBackAction) {
      customBackAction();
    } else {
      navigate(-1);
    }
  };

  const navigationItems = [
    { 
      name: '홈', 
      href: '/', 
      icon: HomeIcon,
      isActive: location.pathname === '/'
    },
    ...(isAuthenticated ? [
      { 
        name: '대시보드', 
        href: '/dashboard', 
        icon: ChartBarIcon,
        isActive: location.pathname === '/dashboard'
      },
      { 
        name: '오답노트', 
        href: '/wrong-answers', 
        icon: BookOpenIcon,
        isActive: location.pathname === '/wrong-answers'
      },
      { 
        name: '학습 진도', 
        href: '/progress', 
        icon: AcademicCapIcon,
        isActive: location.pathname === '/progress'
      }
    ] : [
      {
        name: '시험 목록',
        href: '/exams',
        icon: AcademicCapIcon,
        isActive: location.pathname === '/exams'
      }
    ])
  ];

  return (
    <div className="bg-white shadow-sm border-b border-orange-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-4">
        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Logo */}
              <Link 
                to="/"
                className="flex items-center space-x-2 hover:scale-105 transition-transform duration-200"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">C</span>
                </div>
                <span className="text-lg font-bold text-orange-600">CBT 편의점</span>
              </Link>
              
              {/* Page Title */}
              {title && (
                <>
                  <div className="text-lg text-gray-300">•</div>
                  <span className="text-lg font-semibold text-gray-900 truncate">{title}</span>
                </>
              )}
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 hover:text-gray-900 transition-colors duration-200"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-5 h-5" />
                ) : (
                  <Bars3Icon className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="mt-4 pb-4 border-t border-gray-200 flex justify-center">
              <div className="w-fit">
                <nav className="mt-4 flex flex-col space-y-2">
                {/* 기본 메뉴 항목들 */}
                <a 
                  href="/#features" 
                  className="block px-2 py-1 text-gray-700 hover:bg-gray-100 transition-colors duration-200 rounded-lg text-center font-medium w-fit mx-auto"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  기능소개
                </a>
                <a 
                  href="/#exams" 
                  className="block px-2 py-1 text-gray-700 hover:bg-gray-100 transition-colors duration-200 rounded-lg text-center font-medium w-fit mx-auto"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  시험종류
                </a>
                {/* 모의고사 기능 임시 비활성화 - AI 품질 개선 후 재활성화 예정
                <button 
                  onClick={() => {
                    navigate('/mock-exams');
                    setIsMobileMenuOpen(false);
                  }}
                  className="block px-2 py-1 text-gray-700 hover:bg-gray-100 transition-colors duration-200 rounded-lg text-center font-medium w-fit mx-auto"
                >
                  모의고사
                </button>
                */}
                <a 
                  href="/#testimonials" 
                  className="block px-2 py-1 text-gray-700 hover:bg-gray-100 transition-colors duration-200 rounded-lg text-center font-medium w-fit mx-auto"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  이용후기
                </a>
                
                {isAuthenticated && (
                  <div className="pt-2 border-t border-gray-200">
                    <button
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center justify-center space-x-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                    >
                      <PowerIcon className="w-5 h-5" />
                      <span className="font-medium">로그아웃</span>
                    </button>
                  </div>
                )}
                </nav>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden md:flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Logo */}
            <Link 
              to="/"
              className="flex items-center space-x-2 hover:scale-105 transition-transform duration-200"
            >
              <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">C</span>
              </div>
              <span className="text-xl font-bold text-orange-600">CBT 편의점</span>
            </Link>
            
            {/* Page Title */}
            {title && (
              <>
                <div className="text-2xl text-gray-300">•</div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
                  {subtitle && <p className="text-gray-600">{subtitle}</p>}
                </div>
              </>
            )}
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden md:flex items-center justify-center flex-1">
            <div className="flex space-x-8 text-sm font-medium">
              <a href="/#features" className="text-gray-600 hover:text-orange-600 transition-colors">기능소개</a>
              <a href="/#exams" className="text-gray-600 hover:text-orange-600 transition-colors">시험종류</a>
              {/* 모의고사 기능 임시 비활성화 - AI 품질 개선 후 재활성화 예정
              <button 
                onClick={() => navigate('/mock-exams')}
                className="text-gray-600 hover:text-orange-600 transition-colors font-medium"
              >
                모의고사
              </button>
              */}
              <a href="/#testimonials" className="text-gray-600 hover:text-orange-600 transition-colors">이용후기</a>
            </div>
          </div>
          
          <div className="flex items-center space-x-6">
            
            {/* User Section */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-bold">
                      {user?.email ? user.email.charAt(0).toUpperCase() : user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{user?.name || 'CBT 학습자'}</div>
                    <div className="text-sm text-gray-600">{user?.email || ''}</div>
                  </div>
                </div>
                
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 hover:text-gray-900 transition-colors duration-200 group"
                >
                  <PowerIcon className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                  <span className="font-medium">로그아웃</span>
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                {rightContent}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommonHeader;